import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { Words } from "../../b2c/ui/Words";
import { accented, useCopy } from "../copy";
import { LOOK, TYPE } from "../look";
import { type Stage, useStage } from "../stage";

const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;

/**
 * Most scenes are "a line + a proof": a text block and one visual (a
 * chart, a dashboard card, a widget). `Split` places both for the current
 * shape, and scales the visual (authored once, at `visualSize`) into the
 * room left for it:
 *
 *   wide    text column left, visual right (the partner page master)
 *   square  text on top, visual below
 *   tall    same as square, with more room for the visual (4:5 feed)
 */

type Box = { x: number; y: number; w: number; h: number };

export type SplitGeom = { text: Box; visual: Box; scale: number };

export const splitGeom = (
  stage: Stage,
  visualSize: { w: number; h: number },
  textH: number,
  /** Square/tall: room kept under the visual (a two-line footnote needs more). */
  bottomRoom?: number,
): SplitGeom => {
  let text: Box;
  let visual: Box;
  if (stage.shape === "wide") {
    text = { x: 140, y: 0, w: 720, h: stage.h };
    visual = { x: 940, y: 90, w: stage.w - 940 - 110, h: stage.h - 180 };
  } else {
    const top = stage.shape === "tall" ? 110 : 64;
    // Room for a footnote under the visual.
    const bottom = bottomRoom ?? (stage.shape === "tall" ? 110 : 84);
    text = { x: 80, y: top, w: stage.w - 160, h: textH };
    const vTop = top + textH + (stage.shape === "tall" ? 50 : 24);
    visual = { x: 60, y: vTop, w: stage.w - 120, h: stage.h - vTop - bottom };
  }
  const scale = Math.min(visual.w / visualSize.w, visual.h / visualSize.h, 1.2);
  return { text, visual, scale };
};

export const Split: React.FC<{
  background: string;
  text: React.ReactNode;
  visual: React.ReactNode;
  visualSize: { w: number; h: number };
  /** Square/tall: height reserved for the text block. */
  textH?: number;
  /** Square/tall: room under the visual (see `splitGeom`). */
  bottomRoom?: number;
  /** Overlays in stage px (footnotes, taps…). */
  children?: React.ReactNode;
}> = ({
  background,
  text,
  visual,
  visualSize,
  textH = 300,
  bottomRoom,
  children,
}) => {
  const stage = useStage();
  const g = splitGeom(stage, visualSize, textH, bottomRoom);
  const wide = stage.shape === "wide";
  return (
    <AbsoluteFill style={{ background, overflow: "hidden" }}>
      <div
        style={{
          position: "absolute",
          left: g.text.x,
          top: g.text.y,
          width: g.text.w,
          height: g.text.h,
          display: "flex",
          flexDirection: "column",
          justifyContent: wide ? "center" : "flex-start",
          alignItems: wide ? "flex-start" : "center",
          textAlign: wide ? "left" : "center",
        }}
      >
        {text}
      </div>
      <div
        style={{
          position: "absolute",
          left: g.visual.x + g.visual.w / 2 - visualSize.w / 2,
          top: g.visual.y + g.visual.h / 2 - visualSize.h / 2,
          width: visualSize.w,
          height: visualSize.h,
          scale: String(g.scale),
        }}
      >
        {visual}
      </div>
      {children}
    </AbsoluteFill>
  );
};

// ---------------------------------------------------------------------------
// Text pieces
// ---------------------------------------------------------------------------

/** "① ÉTAPE 1 · CAMPAGNE" — small label above a headline. */
export const Kicker: React.FC<{
  text: string;
  step?: number;
  at?: number;
  dark?: boolean;
}> = ({ text, step, at = 0, dark = false }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const copy = useCopy();
  const p = spring({
    frame: frame - at,
    fps,
    config: { damping: 14, stiffness: 220, mass: 0.6 },
  });
  const ink = dark ? LOOK.white : LOOK.blue;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 18,
        marginBottom: 26,
        opacity: at <= 0 ? 1 : Math.min(1, p * 2),
        translate: at <= 0 ? undefined : `0 ${(1 - p) * 24}px`,
      }}
    >
      {step ? (
        <div
          style={{
            width: 60,
            height: 60,
            borderRadius: 60,
            background: dark ? LOOK.white : LOOK.blue,
            color: dark ? LOOK.blue : LOOK.white,
            fontFamily: LOOK.display,
            fontWeight: LOOK.bold,
            fontSize: 34,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            scale: at <= 0 ? 1 : p,
          }}
        >
          {step}
        </div>
      ) : null}
      <div
        style={{
          fontFamily: LOOK.body,
          fontWeight: LOOK.bold,
          fontSize: TYPE.label - 2,
          letterSpacing: 3,
          textTransform: "uppercase",
          color: ink,
        }}
      >
        {step ? `${copy.steps.label(step)} · ${text}` : text}
      </div>
    </div>
  );
};

/** Kinetic headline; `*word*` in the copy is painted Frak blue. */
export const Headline: React.FC<{
  text: string;
  start?: number;
  dark?: boolean;
  size?: number;
  stagger?: number;
}> = ({ text, start = 0, dark = false, size, stagger = 3 }) => {
  const stage = useStage();
  const { text: plain, accents } = accented(
    text,
    dark ? LOOK.blueSoft : LOOK.blue,
  );
  return (
    <Words
      text={plain}
      start={start}
      stagger={stagger}
      fontSize={size ?? TYPE.headline[stage.shape]}
      color={dark ? LOOK.white : LOOK.ink}
      accents={accents}
      align={stage.shape === "wide" ? "left" : "center"}
      lineHeight={1.04}
    />
  );
};

/** Supporting line under a headline. */
export const Sub: React.FC<{ text: string; at: number; dark?: boolean }> = ({
  text,
  at,
  dark = false,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const p = spring({
    frame: frame - at,
    fps,
    config: { damping: 20, stiffness: 180, mass: 0.7 },
  });
  return (
    <div
      style={{
        marginTop: 26,
        fontFamily: LOOK.body,
        fontWeight: LOOK.medium,
        fontSize: TYPE.support,
        lineHeight: 1.25,
        letterSpacing: -0.4,
        color: dark ? LOOK.sky : LOOK.inkSoft,
        textWrap: "balance",
        opacity: p,
        translate: `0 ${(1 - p) * 26}px`,
      }}
    >
      {text}
    </div>
  );
};

/** Source / disclaimer line pinned to the bottom of the frame. */
export const Footnote: React.FC<{
  text: string;
  at: number;
  dark?: boolean;
}> = ({ text, at, dark = false }) => {
  const frame = useCurrentFrame();
  const stage = useStage();
  const wide = stage.shape === "wide";
  return (
    <div
      style={{
        position: "absolute",
        left: wide ? 140 : 80,
        right: wide ? 140 : 80,
        bottom: stage.shape === "tall" ? 48 : 30,
        textAlign: wide ? "left" : "center",
        fontFamily: LOOK.body,
        fontSize: TYPE.legal,
        lineHeight: 1.3,
        color: dark ? LOOK.white : LOOK.inkSoft,
        opacity: interpolate(
          frame,
          [at, at + 8],
          [0, dark ? 0.75 : 0.85],
          clamp,
        ),
      }}
    >
      {text}
    </div>
  );
};
